const handleize = (str) => {
  return str
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const convertToObject = (arr) => {
  const result = {};
  
  arr.forEach(item => {
    const [key, value] = item.split(":");
    result[key] = value;
  });
  
  return result;
}

function parseInventoryData(dataStr) {
  const result = {};

  const handleSections = dataStr.split(";");

  for (const section of handleSections) {
    if (!section.includes("=")) continue;

    const [handle, variantsStr] = section.split("=");
    const variants = variantsStr.split(",");

    const variantObj = {};

    for (const entry of variants) {
      if (!entry.trim()) continue;

      const [variantId, qty] = entry.split(":");
      const quantity = qty && !isNaN(qty) ? Number(qty.trim()) : 0;

      variantObj[variantId.trim()] = quantity;
    }

    result[handle.trim()] = variantObj;
  }

  return result;
}

function mergeQuantitiesIntoProducts(products, productQuantities, patternTypesArr = []) {
  const patterns = convertToObject(patternTypesArr) || {};

  return products.map(product => {
    const handle = product.handle.trim();
    const quantities = productQuantities[handle] || {};
    const pattern = patterns[handle] || null

    const {
      description,
      content,
      ...cleanedProduct
    } = product;

    const variantsWithQty = cleanedProduct.variants.map(variant => ({
      ...variant,
      quantity: quantities[variant.id] ?? 0,
    }));

    return {
      ...cleanedProduct,
      variants: variantsWithQty,
      pattern
    };
  });
}

class ProductVariantPicker extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.renderRelatedProducts();

    this.colorWrapper.setAttribute("data-pattern", this.patternType);

    this.colorWrapper?.addEventListener("click", this.onButtonClick.bind(this), { signal });
    this.patternSelectors.forEach(input => {
      input?.addEventListener("change", this.onPatternSelect.bind(this), { signal });
    });

    this.sizeWrapper?.addEventListener("click", this.onSizeBtnClick.bind(this), { signal });

    this.selectedColor = this.selectedColorName;

    window.prefetchedProductsHTML = window.prefetchedProductsHTML || {};

    this.initFitModal();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  // ===================
  // GETTERS / SETTERS
  // ===================
  get colorSwatches() {
    return this.dataset.colorSwatches;
  }

  get styleCode() {
    return this.dataset.styleCode; 
  }

  get productId() {
    return Number(this.dataset.productId);
  }

  get colorWrapper() {
    return this.querySelector("[data-color-wrapper]");
  }

  get productHandle() {
    return this.dataset.productUrl.replace("/products/", "");
  }

  get patternType() {
    return this.dataset.patternType;
  }

  get patternWrapper() {
    return this.querySelector("[data-pattern-wrapper]");
  }

  get patternSelectors() {
    return this.querySelectorAll("[data-pattern-btn]");
  }

  set patternSelector(value) {
    this.patternSelectors.forEach(input => {
      input.checked = input.value == value;
    });
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get selectedColorField() {
    return this.querySelector("[data-selected-color]");
  }

  set selectedColor(value) {
    this.selectedColorField.innerText = value || "";
  }

  get selectedColorName() {
    return this.querySelector(".pf-color-swatch.is-selected")?.dataset.colorName;
  }

  get sizeWrapper() {
    return document.querySelector("variant-picker:has(#pf-size-variants)");
  }

  get selectedSize() {
    const selectedSizeValue = window.pfSizeStreak || this.sizeWrapper.querySelector("#pf-size-variants .variant-radio input:checked")?.value;

    return selectedSizeValue;
  }

  get sizeButtons() {
    return Array.from(this.sizeWrapper.querySelectorAll("#pf-size-variants .variant-radio"));
  }

  get swatchOrders() {
    const swatches = (this.dataset.swatchOrders || "").split("|");
    
    const solid = (swatches[0] || "")?.replace("solid:", "").split(",");
    const printed = (swatches[1] || "")?.replace("printed:", "").split(",");

    return {
      solid,
      printed
    };
  }

  // ===================
  // MAIN HANDLER
  // ===================
  async onButtonClick(e) {
    const button = e.target.closest("[data-color-btn]");
    if (!button) return;

    const handle = button.dataset.productHandle;
    if(handle == this.productHandle) return;

    this.updateSelectedSwatch(button);

    const colorWrapperCopy = this.colorWrapper.cloneNode(true);
    const patternWrapperCopy = this.patternWrapper.cloneNode(true);

    const productId = button.dataset.productId;

    if (window.prefetchedProductsHTML?.[handle]) {
      this.updateProductPage(window.prefetchedProductsHTML[handle], colorWrapperCopy, patternWrapperCopy, `${window.Shopify.routes.root}products/${handle}`, productId);
    } else {
      const url = `${window.Shopify.routes.root}products/${handle}`;
      try {
        const html = await this.fetchProductPage(url);
        this.updateProductPage(html, colorWrapperCopy, patternWrapperCopy, url, productId);
        
        window.prefetchedProductsHTML[handle] = html;
      } catch (err) {
        console.error("Error updating product page:", err);
      }
    }

    // const sizeInput = this.sizeWrapper.querySelector(`#pf-size-variants .variant-radio input[value="${this.selectedSize}"]`);
    
    // if(sizeInput) {
    //   sizeInput.checked = true;
    //   this.sizeWrapper.handleChange({ target: sizeInput });
    // }

    var load_event = new Event('load');
    window.dispatchEvent(load_event);
  }

  onPatternSelect(e) {
    const pattern = e.target.value;
    const selector = `.pf-color-swatch[data-pattern="${pattern}"]`;
    const button = this.colorWrapper.querySelector(selector);

    if(button) {
      this.colorWrapper.setAttribute("data-pattern", pattern);
      button.click();
    } else {
      this.patternSelector = pattern == "solid" ? "printed" : "solid";
    }
  }

  onSizeBtnClick(e) {
    const variantRadio = e.target.closest("#pf-size-variants .variant-radio");
    if(!variantRadio) return;

    const input = variantRadio.querySelector("input");

    window.pfSizeStreak = input.value;
  }

  // ===================
  // HELPER FUNCTIONS
  // ===================

  updateSelectedSwatch(button) {
    document.querySelector(".pf-color-swatch.is-selected")?.classList.remove("is-selected");
    button.classList.add("is-selected");
  }

  async fetchProductPage(url) {
    const res = await fetch(url + "?section_id=" + this.sectionId);
    return res.text();
  }

  updateProductPage(html, colorWrapperCopy, patternWrapperCopy, url, productId, selectedSize) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    doc.querySelector("[data-color-wrapper]").replaceWith(colorWrapperCopy);

    doc.querySelector("[data-pattern-wrapper]").setAttribute("data-solid", patternWrapperCopy.dataset.solid);
    doc.querySelector("[data-pattern-wrapper]").setAttribute("data-printed", patternWrapperCopy.dataset.printed);

    const domSectionId = `#shopify-section-${this.sectionId}`;

    document.querySelector(domSectionId).innerHTML = doc.querySelector(domSectionId).innerHTML;

    const colorMixerEl = document.querySelector("pf-color-mixer");
    colorMixerEl?.renderVariantTiles?.();

    if(window.Zip && window.Zip.Widget && window.Zip.Widget.render) {
      window.Zip.Widget.render()
    }

    const junipBlock = document.querySelector(".junip-product-review");

    junipBlock?.setAttribute("data-product-id", productId);
    junipBlock?.setAttribute("id", `junipProductReviews${productId}`);

    document.title = doc.title;
    this.updateMetaTags(doc);

    this.reloadRechargeWidget(doc);

    window.history.pushState({}, "", url);

    this.loadAsyncScripts();
  }

  updateMetaTags(doc) {
    const newMetas = doc.head.querySelectorAll("meta[name], meta[property]");
    document.querySelectorAll("meta[name], meta[property]").forEach(m => m.remove());
    newMetas.forEach(m => document.head.appendChild(m));
  }

  reloadRechargeWidget(doc) {
    const appBlock = document.querySelector(".recharge-subscription-widget");
    if(!appBlock) return;

    const appBlockChildren = Array.from(doc.querySelectorAll(".recharge-subscription-widget > *"));
    appBlock.innerHTML = "";

    appBlockChildren.forEach(child => {
      if (child.tagName === "SCRIPT") {
        const newScript = document.createElement("script");
        Array.from(child.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
        if (child.textContent) newScript.textContent = child.textContent;
        appBlock.appendChild(newScript);
      } else {
        appBlock.appendChild(child.cloneNode(true));
      }
    });
  }

  loadSizeChart() {
    const existingScript = document.querySelector("[src*='size_chart.js']");

    const newScript = document.createElement("script");
    newScript.src = existingScript.src;

    existingScript.remove();

    document.querySelector("#lavar-size-chart").insertAdjacentElement("beforebegin", newScript);
  }

  loadAsyncScripts() {
    this.loadSizeChart();
    
    const urls = [
      "https://static.rechargecdn.com/assets/js/widget.min.js?shop=tradie-au.myshopify.com",
      "https://widgets.juniphq.com/v1/junip_shopify.js?shop=tradie-au.myshopify.com",
      "https://chimpstatic.com/mcjs-connected/js/users/b4dc8c17a306fc7ea974d3f43/0c97f6952801355aae1c2be26.js?shop=tradie-au.myshopify.com"
    ];

    urls.forEach(src => {
      if (!document.querySelector(`script[src="${src}"]`)) {
        const s = document.createElement("script");
        s.type = "text/javascript";
        s.async = true;
        s.src = src;
        document.head.appendChild(s);
      }
    });

    const widgetContainer = document.querySelector(".recharge-subscription-widget");
    if (widgetContainer && !widgetContainer.querySelector("script[type='module']")) {
      const moduleScript = document.createElement("script");
      moduleScript.type = "module";
      moduleScript.src = "https://static.rechargecdn.com/assets/subscription-widget/subscription-widget.js";
      widgetContainer.appendChild(moduleScript);
    }
  }

  // ===================
  // RELATED PRODUCTS
  // ===================
  async renderRelatedProducts(styleCode = null) {
    const code = styleCode || this.styleCode;
    if(!code) return;

    const productsData = await this.getRelatedProducts(code);
    let products = productsData.products;

    // this.productsWithQuantities = products.sort((a, b) =>
    //   (a.pattern === "solid" ? 0 : a.pattern === "pattern" ? 1 : 2) -
    //   (b.pattern === "solid" ? 0 : b.pattern === "pattern" ? 1 : 2)
    // );

    const patterns = ["solid", "printed"];
    this.productsWithQuantities = [];

    patterns.forEach(pattern => {
      const patternProducts =  products.filter(p => p.pattern == pattern);
      const swatchOrders = this.swatchOrders?.[pattern] || [];

      swatchOrders.forEach(color => {
        const ind = patternProducts.findIndex(p => p.variants?.[0]?.option1?.trim().toLowerCase() == color.trim().toLowerCase());

        if(ind != -1) {
          this.productsWithQuantities.push(patternProducts[ind]);
          patternProducts.splice(ind, 1);
        }
      });

      if(patternProducts.length > 0) this.productsWithQuantities.push(...patternProducts);
    });

    if(products.length != this.productsWithQuantities.length) {
      const restOfProducts = products.filter(p => !patterns.includes(p.pattern));
      this.productsWithQuantities.push(...restOfProducts);
    }

    products = this.productsWithQuantities;

    this.patternTypes = productsData.patterns;
    
    this.patternWrapper.setAttribute("data-solid", productsData.solidPatterns);
    this.patternWrapper.setAttribute("data-printed", productsData.printedPatterns);
    this.colorWrapper.setAttribute("data-patterns", productsData.totalPatterns);
    this.colorWrapper.setAttribute("data-products", products.length);

    if(!window.relatedProductsLoaded) {
      this.colorWrapper.querySelectorAll("[data-color-btn]")?.forEach(btn => btn.remove());

      products.sort((a, b) => {
        if (a.id === this.productId) return -1;
        if (b.id === this.productId) return 1;
        return 0;
      });

      products.forEach(product => {
        if (!(product.variants?.length > 0)) return;
        const color = product.variants[0].option1;
        if (!color) return;

        const buttonEl = this.getColorButton(color, product.handle, product.id === this.productId, product.id,  product.featured_image);
        if (buttonEl) this.colorWrapper.insertAdjacentHTML("beforeend", buttonEl);
      });
    }

    window.relatedProductsLoaded = true;
    
    await Promise.all(products.map(async (product) => {
      if (!window.prefetchedProductsHTML[product.handle]) {
        const url = `${window.Shopify.routes.root}products/${product.handle}`;
        const res = await fetch(url);

        window.prefetchedProductsHTML[product.handle] = await res.text();
      }
    }));

    requestAnimationFrame(() => {
      const carousel = this.querySelector(".pf-color-carousel");
      if (carousel && window.pfUpdateCarouselArrows) window.pfUpdateCarouselArrows(carousel);
    });
  }

  async getRelatedProducts(styleCode) {
    try {
      const productsObj = {
          products: [],
          patterns: {},
          totalPatterns: 0,
          solidPatterns: 0,
          printedPatterns: 0
        };

      const url = `/collections/all?filter.p.m.custom.ap21_style_code=${styleCode}&section_id=main-collection-grid`;
      const response = await fetch(url);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const jsonElement = doc.querySelector(".pf-products-json");

      if (!jsonElement) {
        console.warn("No .pf-products-json element found in response");

        return productsObj;
      }

      try {
        productsObj.products = JSON.parse(jsonElement.dataset.products);

        // const filteredProducts = productsObj.products.filter(product => {
        //   return product?.selling_plan_groups?.length == 0 || this.dataset.productId == product.id;
        // });

        const filteredProducts = productsObj.products;

        const productQuantities = parseInventoryData(jsonElement.dataset.productQuantities);

        const patternTypesArr = (jsonElement.dataset.patternTypes || "").split(",");
        productsObj.products = mergeQuantitiesIntoProducts(filteredProducts, productQuantities, patternTypesArr);

        patternTypesArr.forEach(patternStr => {
          const patternArr = (patternStr || "").split(":");
          
          if(!patternArr[0]) return;

          if(patternArr[1] == "solid") {
            productsObj.solidPatterns++;
            productsObj.totalPatterns++;
          } else if(patternArr[1] == "printed") {
            productsObj.printedPatterns++;
            productsObj.totalPatterns++;
          }
          
          productsObj.patterns[patternArr[0]] = (patternArr[1] || "");
        })
        
        return productsObj;
      } catch (err) {
        console.error("Error parsing .pf-products-json JSON", err);
        return productsObj;
      }
    } catch (error) {
      console.error("Error loading related products:", error);
      return productsObj;
    }
  }

  async getProductData(handle) {
    if (this.productsWithQuantities) {
      return handle ? this.productsWithQuantities.find(p => p.handle === handle) || null : this.productsWithQuantities;
    }

    await this.#waitForProductsData();

    return handle ? this.productsWithQuantities.find(p => p.handle === handle) || null : this.productsWithQuantities;
  }

  // ===================
  // BUTTON BUILDER
  // ===================
  getColorButton(color, productHandle, isActive, productId, featuredImage) {
    const pattern = this.patternTypes[productHandle];
    // if(!pattern && !isActive) return null;
    
    const currentColor = handleize(color);
    const swatchLines = this.colorSwatches.split(/\r?\n/);

    let colorValue = null;
    swatchLines.forEach(line => {
      const [name, value] = line.split(":").map(s => s.trim());
      if (!name || !value) return;
      
      if (currentColor == handleize(name)) {
        colorValue = value;
      }
    });

    let style = /\.(jpg|png)$/i.test(colorValue)
      ? `background-image: url('https://cdn.shopify.com/s/files/1/0645/1996/2668/files/${colorValue}')`
      : `background: linear-gradient(to right, ${colorValue}, ${colorValue})`;

    if (!colorValue) style = `background-image: url('${featuredImage}')`;

    return `
      <button
        class="pf-color-swatch ${isActive ? 'is-selected' : ''}"
        data-color-name="${color}"
        data-product-handle="${productHandle}"
        data-product-id="${productId}"
        data-pattern="${pattern ? pattern : "solid"}"
        data-color-btn
      >
        <span class="pf-color-icon" style="${style};"></span>
        <p class="pf-color-tooltip"><span>${color}</span></p>
      </button>
    `;
  }

  #waitForProductsData() {
    return new Promise((resolve, reject) => {
      const start = performance.now();

      const check = () => {
        if (this.productsWithQuantities) return resolve();

        if (performance.now() - start > 10000) {
          return reject(new Error("Timeout waiting for productsWithQuantities"));
        }
        requestAnimationFrame(check);
      };

      check();
    });
  }


  // ===================
  // Extra Script Load
  // ===================
  initFitModal() {
    var modal = document.getElementById('fitModal');
    var overlay = document.getElementById('modalOverlay');
    var btn = document.getElementById('openFitModal');
    var span = document.getElementsByClassName('close-fit-modal')[0];

    if(span){
      span.onclick = function () {
        closeModal();
      };
    }

    if(overlay){
      overlay.onclick = function () {
        closeModal();
      };
    }

    function closeModal() {
      modal.classList.remove('open');
      setTimeout(function () {
        modal.style.display = 'none';
        overlay.classList.remove('open');
      }, 300);
    }
  }
}

if(!customElements.get("product-variant-picker")) {
  customElements.define("product-variant-picker", ProductVariantPicker);
}

// ===================
// Swatch carousel: mouse drag-to-scroll + wheel-to-horizontal
// (native overflow-x scroll only supports trackpad/shift+wheel;
// this adds plain-mouse support and prevents the drag from
// triggering a swatch click.)
// ===================
(function () {
  const CAROUSEL_SELECTOR = ".pf-color-variant-wrapper";

  document.addEventListener("wheel", (e) => {
    const carousel = e.target.closest(CAROUSEL_SELECTOR);
    if (!carousel) return;
    if (carousel.scrollWidth <= carousel.clientWidth) return;

    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      carousel.scrollLeft += e.deltaY;
      e.preventDefault();
    }
  }, { passive: false });

  let dragState = null;

  document.addEventListener("mousedown", (e) => {
    const carousel = e.target.closest(CAROUSEL_SELECTOR);
    if (!carousel) return;

    dragState = {
      carousel,
      startX: e.pageX,
      startScrollLeft: carousel.scrollLeft,
      moved: false,
    };
    carousel.classList.add("is-dragging");
  });

  document.addEventListener("mousemove", (e) => {
    if (!dragState) return;

    const dx = e.pageX - dragState.startX;
    if (Math.abs(dx) > 3) dragState.moved = true;

    dragState.carousel.scrollLeft = dragState.startScrollLeft - dx;
  });

  const endDrag = () => {
    if (!dragState) return;
    dragState.carousel.classList.remove("is-dragging");

    if (dragState.moved) {
      const carousel = dragState.carousel;
      const suppressClick = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        carousel.removeEventListener("click", suppressClick, true);
      };
      carousel.addEventListener("click", suppressClick, true);
    }

    dragState = null;
  };

  document.addEventListener("mouseup", endDrag);
  document.addEventListener("mouseleave", endDrag);
})();

// ===================
// Swatch carousel: prev/next arrow buttons
// ===================
(function () {
  const CAROUSEL_SELECTOR = ".pf-color-carousel";
  const WRAPPER_SELECTOR = ".pf-color-variant-wrapper";

  function getParts(carousel) {
    return {
      wrapper: carousel.querySelector(WRAPPER_SELECTOR),
      prevBtn: carousel.querySelector("[data-carousel-prev]"),
      nextBtn: carousel.querySelector("[data-carousel-next]"),
    };
  }

  function updateArrows(carousel) {
    const { wrapper, prevBtn, nextBtn } = getParts(carousel);
    if (!wrapper) return;

    const hasOverflow = wrapper.scrollWidth > wrapper.clientWidth + 2;
    const atStart = wrapper.scrollLeft <= 2;
    const atEnd = wrapper.scrollLeft >= wrapper.scrollWidth - wrapper.clientWidth - 2;

    if (prevBtn) prevBtn.hidden = !hasOverflow || atStart;
    if (nextBtn) nextBtn.hidden = !hasOverflow || atEnd;
  }

  function updateAllArrows() {
    document.querySelectorAll(CAROUSEL_SELECTOR).forEach(updateArrows);
  }

  window.pfUpdateCarouselArrows = updateArrows;

  function scrollCarousel(carousel, direction) {
    const { wrapper } = getParts(carousel);
    if (!wrapper) return;

    const swatch = wrapper.querySelector(".pf-color-swatch");
    const step = swatch ? swatch.getBoundingClientRect().width + 10 : 100;

    wrapper.scrollBy({ left: direction * step * 3, behavior: "smooth" });
  }

  document.addEventListener("click", (e) => {
    const prevBtn = e.target.closest("[data-carousel-prev]");
    const nextBtn = e.target.closest("[data-carousel-next]");
    if (!prevBtn && !nextBtn) return;

    const carousel = (prevBtn || nextBtn).closest(CAROUSEL_SELECTOR);
    if (!carousel) return;

    scrollCarousel(carousel, prevBtn ? -1 : 1);
  });

  document.addEventListener("scroll", (e) => {
    const target = e.target;
    if (!target || !target.closest) return;

    const wrapper = target.closest(WRAPPER_SELECTOR);
    if (!wrapper) return;

    const carousel = wrapper.closest(CAROUSEL_SELECTOR);
    if (carousel) updateArrows(carousel);
  }, true);

  window.addEventListener("resize", updateAllArrows);
  window.addEventListener("load", updateAllArrows);
  document.addEventListener("DOMContentLoaded", updateAllArrows);

  updateAllArrows();
})();
